'use client';
import { projectName } from '../../../packages/plugin-shared/projectChoice.mjs';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import type { SwitchableProject } from '../../../src/shared/wireContract';
import { useTranslation } from '../../lib/i18n';
import { normalizeText } from '../../lib/normalizeText';
import { executionRefKey } from '../../lib/types';
import { Input } from './Input';
import { ProjectIcon } from './ProjectIcon';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from './shadcn/dropdown-menu';

interface ProjectMenuContentProps {
  /** The menu's accessible name. */
  label: string;
  align: 'start' | 'end';
  projects: readonly SwitchableProject[];
  /** The `executionRefKey` of the current project. Given, the rows are radio items that check it; omitted,
   *  they are plain items, for a menu that creates something new rather than switching. */
  selected?: string;
  /** Show the execution kind (managed environment or host folder) under each name. */
  showKind?: boolean;
  disabled?: boolean;
  onChoose: (project: SwitchableProject) => void;
  /** Shown instead of the search and the rows while the surface has no list to offer (loading, failed,
   *  empty); the surface owns those states. */
  status?: ReactNode;
  'data-testid'?: string;
}

/** The body of every project dropdown: the chat header's project picker, the dashboard composer's
 *  destination pill and the new-conversation menu. Each surface keeps its own trigger, what choosing does
 *  and its loading and empty states; this owns the panel, the search field and the rows.
 *
 *  The panel itself does not scroll: the field sits in a fixed head above a hairline, and only
 *  the rows below it scroll, so the scrollbar never runs beside the field. */
export function ProjectMenuContent({ label, align, status, 'data-testid': testId, ...list }: ProjectMenuContentProps) {
  return (
    <DropdownMenuContent aria-label={label} align={align} collisionPadding={8} data-testid={testId}
      className="flex w-72 max-w-[calc(100vw-var(--spacing)*4)] max-h-[min(--spacing(128),var(--radix-dropdown-menu-content-available-height))] flex-col overflow-hidden p-0">
      {status === undefined ? <ProjectMenuSearchList {...list} /> : (
        <div className="min-h-0 overflow-y-auto p-1.5">
          {status}
        </div>
      )}
    </DropdownMenuContent>
  );
}

/** The term lives in this component, which Radix unmounts with the content on close, so every open
 *  starts from the whole list. The match ignores case AND diacritics (`normalizeText`), so "skoleni"
 *  finds "Školení". */
function ProjectMenuSearchList({ projects, selected, showKind, disabled, onChoose }: Omit<ProjectMenuContentProps, 'label' | 'align' | 'status' | 'data-testid'>) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const needle = normalizeText(query.trim());
  const matches = needle ? projects.filter((project) => normalizeText(`${projectName(project)} ${project.path}`).includes(needle)) : projects;
  const row = (project: SwitchableProject) => (
    <>
      <ProjectIcon project={project} size={showKind ? 20 : 16} />
      {/* A paired computer is one line: its name, then its status, muted. Neither its home nor a
          conversation's working directory belongs in the row that picks the computer itself. */}
      {project.computer ? (
        <span className="flex min-w-0 flex-1 items-baseline gap-1">
          <span className="min-w-0 truncate">{projectName(project)}</span>
          <span className="shrink-0 text-small text-muted-foreground">
            · {t.projects.computer} · {project.computer.connected ? t.elowenCli.connected : t.elowenCli.offline}
          </span>
        </span>
      ) : showKind ? (
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{projectName(project)}</span>
          <span className="truncate text-small text-muted-foreground">
            {project.executionRef.kind === 'managed' ? t.projects.managed : t.projects.host}
          </span>
        </span>
      ) : <span className="min-w-0 flex-1 truncate">{projectName(project)}</span>}
    </>
  );
  return (
    <>
      <div className="shrink-0 border-b border-border p-2">
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t.projects.menuSearch}
          aria-label={t.projects.menuSearch}
          autoComplete="off"
          spellCheck={false}
          data-testid="project-menu-search"
          // `chat-project-search` (app/styles/components/chat.css) holds the box-shadow at none in every
          // state: the owner decided this field shows the caret and nothing else, because a ring lighting
          // up inside an open menu reads as a second overlay. Row height rather than the primitive's field
          // height, and Chrome's own cancel button wears browser styling no token reaches.
          className="chat-project-search h-8 py-0 pl-2.5 pr-2.5 focus-visible:border-input [&::-webkit-search-cancel-button]:hidden"
          onKeyDown={handOffKeys}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {matches.length === 0 ? (
          <DropdownMenuLabel className="italic" data-testid="project-menu-no-match">{t.projects.menuNoMatch}</DropdownMenuLabel>
        ) : selected === undefined ? matches.map((project) => (
          <DropdownMenuItem key={executionRefKey(project.executionRef)} disabled={disabled || offline(project)} onSelect={() => onChoose(project)}>
            {row(project)}
          </DropdownMenuItem>
        )) : (
          <DropdownMenuRadioGroup value={selected}>
            {matches.map((project) => (
              <DropdownMenuRadioItem key={executionRefKey(project.executionRef)} value={executionRefKey(project.executionRef)}
                disabled={disabled || offline(project)} onSelect={() => onChoose(project)}>
                {row(project)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        )}
      </div>
    </>
  );
}

/** An offline computer stays in the list — a reader looking for their machine should find it and see
 *  why it cannot be chosen — but it cannot be picked: the daemon refuses a turn on a computer whose
 *  connection is down, and a row that silently fails is worse than a disabled one. */
const offline = (project: SwitchableProject): boolean => project.computer?.connected === false;

/** The field's keys are its own except the two the list needs. The menu's typeahead runs on the CONTENT
 *  and would pull the caret into the first row matching the first character typed, so printable keys stop
 *  here; ArrowDown and ArrowUp hand focus to the filtered rows, so the menu's own keyboard navigation and
 *  Enter reach them; Escape and Tab still travel to Radix. */
function handOffKeys(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    const rows = event.currentTarget.closest('[data-radix-menu-content]')
      ?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([data-disabled]), [role="menuitemradio"]:not([data-disabled])');
    if (!rows?.length) return;
    rows[event.key === 'ArrowDown' ? 0 : rows.length - 1]!.focus();
    return;
  }
  if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) event.stopPropagation();
}
